import { FEEDBACK_DRAFT_PART, MEMORY_PROPOSAL_PART, WIKI_SUGGESTION_PART } from "@esi/shared";
import type { ChatMessage } from "./MessageView.tsx";

// A streaming chat can outlive its mounted Thread. Keep its last authorized baseline
// with that chat so remounting cannot mistake newly hidden history for a benign update.
export const authorizedHistory = new WeakMap<object, ChatMessage[]>();

// Proposal decisions intentionally remove the server proposal while the mounted card shows
// its confirmation. Only persisted message/redaction changes replace local parts.
const persisted = (message: ChatMessage) =>
  JSON.stringify({
    role: message.role,
    hidden: message.metadata?.hidden === true,
    parts: message.parts.filter(
      (part) =>
        part.type !== MEMORY_PROPOSAL_PART &&
        part.type !== WIKI_SUGGESTION_PART &&
        // Streamed only (feedback-from-conversation): the stored draft is read on its own.
        part.type !== FEEDBACK_DRAFT_PART,
    ),
  });

type SuggestionPart = { type: typeof WIKI_SUGGESTION_PART; data?: { decided?: unknown } };
const suggestionPart = (message: ChatMessage) =>
  message.parts.find((part) => part.type === WIKI_SUGGESTION_PART) as SuggestionPart | undefined;

/**
 * An undecided wiki suggestion follows the server's (its `blocked` reason, or gone once expired,
 * revoked or decided elsewhere); a decision taken here stays until the chat is dropped.
 */
function syncSuggestion(local: ChatMessage, fresh: ChatMessage): ChatMessage {
  const mine = suggestionPart(local);
  if (mine?.data?.decided) {
    return local;
  }
  const theirs = suggestionPart(fresh);
  if (JSON.stringify(mine ?? null) === JSON.stringify(theirs ?? null)) {
    return local;
  }
  const parts = local.parts.filter((part) => part.type !== WIKI_SUGGESTION_PART);
  return { ...local, parts: (theirs ? [...parts, theirs] : parts) as ChatMessage["parts"] };
}

export function reconcileAuthorizedHistory(
  local: ChatMessage[],
  previous: ChatMessage[],
  current: ChatMessage[],
  streaming = false,
): ChatMessage[] {
  const before = new Map(previous.map((message) => [message.id, message]));
  const now = new Map(current.map((message) => [message.id, message]));
  const positions = new Map(current.map((message, index) => [message.id, index]));
  const localIds = new Set(local.map((message) => message.id));
  const replacements = new Map<string, ChatMessage>();
  // The server mints user IDs, but streams the persisted assistant ID. That shared
  // answer identifies its immediately preceding question, even for identical turns.
  for (let i = 1; i < local.length; i++) {
    const answer = local[i];
    const question = local[i - 1];
    if (answer?.role !== "assistant" || question?.role !== "user" || now.has(question.id)) {
      continue;
    }
    const index = positions.get(answer.id) ?? -1;
    const stored = current[index - 1];
    if (stored?.role === "user" && !localIds.has(stored.id)) {
      replacements.set(question.id, stored);
    }
  }
  // A started answer may leave only its committed question in detail. Its streamed
  // assistant proves acceptance; a lone failed/pending local user proves nothing.
  // Reconcile only this sole tail position, never identical questions globally.
  let localBoundary = -1;
  let currentBoundary = -1;
  for (let i = 0; i < local.length; i++) {
    const index = positions.get(local[i]?.id ?? "") ?? -1;
    if (index >= 0) {
      localBoundary = i;
      currentBoundary = index;
    }
  }
  const localTail = local.slice(localBoundary + 1);
  const currentTail = current.slice(currentBoundary + 1);
  if (currentTail.length === 1 && localTail.length === 2 && localTail[1]?.role === "assistant") {
    const question = localTail[0];
    const stored = currentTail[0];
    if (
      question?.role === "user" &&
      stored?.role === "user" &&
      (stored.metadata?.hidden || (!streaming && persisted(question) === persisted(stored)))
    ) {
      replacements.set(question.id, stored);
    }
  }
  const replacedIds = new Set([...replacements.values()].map((message) => message.id));
  let changed = false;
  const result = local.flatMap((message) => {
    const replacement = replacements.get(message.id);
    if (replacement) {
      changed = true;
      return [replacement];
    }
    const old = before.get(message.id);
    const fresh = now.get(message.id);
    if (old && !fresh) {
      changed = true;
      return [];
    }
    if (fresh && (!old || (old !== fresh && persisted(old) !== persisted(fresh)))) {
      changed = true;
      return [fresh];
    }
    if (fresh && !streaming) {
      const synced = syncSuggestion(message, fresh);
      if (synced !== message) {
        changed = true;
        return [synced];
      }
    }
    return [message];
  });
  for (const message of current) {
    if (!localIds.has(message.id) && !replacedIds.has(message.id)) {
      // During a turn the detail can contain the committed user before its answer.
      // Retry this pending identity on later renders, even if current === previous
      // or this streaming chat was remounted from the cache.
      if (
        streaming &&
        message.role === "user" &&
        local.some((item) => item.role === "user" && !now.has(item.id))
      ) {
        continue;
      }
      changed = true;
      result.push(message);
    }
  }
  return changed ? result : local;
}
